import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useCart from '../hooks/useCart';
import useAuth from '../hooks/useAuth';
import AddressForm from '../components/Checkout/AddressForm.jsx';
import PaymentForm from '../components/Checkout/PaymentForm.jsx';
import OrderConfirmation from './OrderConfirmationPage.jsx';
import '../components/Checkout/CheckoutStyles.css';
import './CheckoutPage.css';
import { bindStorefrontRefresh } from '../utils/storefrontRefresh';
import { calculateShippingPrice } from '../utils/orderPricing';

const CheckoutPage = ({ initialStep = 1 }) => {
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [paymentCompleted, setPaymentCompleted] = useState(initialStep === 3);
  
  const { user, loading: authLoading } = useAuth();
  const { cart, getTotalPrice, syncCartProducts } = useCart();
  const navigate = useNavigate();
  
  // Calculate prices
  const itemsPrice = getTotalPrice();
  const shippingPrice = calculateShippingPrice(itemsPrice);
  const taxPrice = 0; // Can be calculated based on your tax rules
  const totalOrderPrice = itemsPrice + shippingPrice + taxPrice;
  const getStockQuantity = (product = {}) => {
    const parsedStockQuantity = Number(product?.stockQuantity);
    return Number.isFinite(parsedStockQuantity) ? Math.max(0, Math.floor(parsedStockQuantity)) : 0;
  };
  const getItemStockQuantity = (item = {}) => {
    const product = item.product || {};
    if (!Array.isArray(product.sizeInventory) || product.sizeInventory.length === 0) {
      return getStockQuantity(product);
    }

    const selectedSize = String(item.selectedSize || '').trim().toLowerCase();
    const sizeEntry = product.sizeInventory.find(
      (entry) => String(entry?.size || '').trim().toLowerCase() === selectedSize
    );
    const parsedQuantity = Number(sizeEntry?.quantity);

    return Number.isFinite(parsedQuantity) ? Math.max(0, Math.floor(parsedQuantity)) : 0;
  };
  const getSelectableSizes = (product = {}) => (
    Array.isArray(product?.availableSizes)
      ? product.availableSizes
        .map((size) => String(size || '').trim())
        .filter((size) => {
          const normalizedSize = String(size || '').trim().toUpperCase();
          return Boolean(normalizedSize && normalizedSize !== 'NA' && normalizedSize !== 'N/A');
        })
      : []
  );
  const getOrderItemSize = (item = {}) => {
    const selectedSize = String(item.selectedSize || '').trim();
    if (selectedSize) {
      return selectedSize;
    }

    const selectableSizes = getSelectableSizes(item.product);
    if (selectableSizes.length === 1) {
      return selectableSizes[0];
    }

    return selectableSizes.length > 1 ? '' : 'NA';
  };
  const hasInvalidStockItems = (items = []) => items.some((item) => {
    const stockQuantity = getItemStockQuantity(item);
    return stockQuantity <= 0 || (Number(item.quantity) || 0) > stockQuantity;
  });
  const normalizePhoneForPayment = (phone = '') => {
    const digits = String(phone || '').replace(/\D/g, '');
    if (digits.length === 12 && digits.startsWith('91')) {
      return digits.slice(2);
    }
    if (digits.length === 11 && digits.startsWith('0')) {
      return digits.slice(1);
    }
    return digits;
  };
  const normalizeShippingAddressForPayment = (address = {}) => ({
    ...address,
    pincode: String(address.pincode || '').replace(/\D/g, ''),
    phone: normalizePhoneForPayment(address.phone),
  });

  // Load order from sessionStorage if we're on the confirmation step
  useEffect(() => {
    if (initialStep === 3 && !order) {
      const savedOrder = sessionStorage.getItem('lastOrder');
      const savedPaymentMethod = sessionStorage.getItem('lastPaymentMethod');
      
      if (savedOrder) {
        setOrder(JSON.parse(savedOrder));
        setPaymentCompleted(true);
      }
      
      if (savedPaymentMethod) {
        setSelectedPaymentMethod(JSON.parse(savedPaymentMethod));
      }
    }
  }, [initialStep, order]);

  useEffect(() => {
    if (user) {
      syncCartProducts();
    }
  }, [user, syncCartProducts]);

  useEffect(() => {
    if (currentStep !== 1 || selectedAddress) {
      return;
    }

    const savedShippingAddress = sessionStorage.getItem('checkoutShippingAddress');
    if (!savedShippingAddress) {
      return;
    }

    try {
      const parsedAddress = JSON.parse(savedShippingAddress);
      if (parsedAddress?.fullName && parsedAddress?.addressLine1 && parsedAddress?.city && parsedAddress?.state && parsedAddress?.pincode && parsedAddress?.phone) {
        setSelectedAddress(parsedAddress);
        setCurrentStep(2);
      }
    } catch {
      sessionStorage.removeItem('checkoutShippingAddress');
    }
  }, [currentStep, selectedAddress]);

  useEffect(() => {
    if (!user) {
      return undefined;
    }

    return bindStorefrontRefresh(() => {
      syncCartProducts();
    }, { scopes: ['products'] });
  }, [user, syncCartProducts]);
  
  useEffect(() => {
    // Redirect if not logged in or cart is empty
    if (!authLoading && !user) {
      navigate('/login', { state: { from: '/checkout' } });
      return;
    }
    
    // Only redirect to cart if cart is empty AND we're not on the confirmation step
    // AND payment has not been completed
    // This prevents redirection after payment is completed and cart is cleared
    if (cart.length === 0 && currentStep !== 3 && !order && !paymentCompleted) {
      navigate('/cart');
      return;
    }
  }, [user, authLoading, cart, navigate, currentStep, order, paymentCompleted]);

  const handleAddressSelect = (address) => {
    setSelectedAddress(address);
  };
  
  const handlePaymentMethodSelect = async (method) => {
    setSelectedPaymentMethod(method);
  };

  const createPaymentPayload = async () => {
    if (!selectedAddress) {
      throw new Error('Please select a shipping address before payment.');
    }

    const syncedCart = await syncCartProducts();

    if (hasInvalidStockItems(syncedCart)) {
      throw new Error('One or more items in your cart are out of stock or exceed the available quantity.');
    }

    const invalidItems = syncedCart.filter(item => {
      const stockQuantity = getItemStockQuantity(item);
      const selectableSizes = getSelectableSizes(item.product);
      const orderItemSize = getOrderItemSize(item);
      return !item.product || !item.product._id || !item.product.title ||
             !item.quantity || !item.product.images ||
             item.product.images.length === 0 ||
             stockQuantity <= 0 ||
             item.quantity > stockQuantity ||
             (selectableSizes.length > 0 && !orderItemSize);
    });

    if (invalidItems.length > 0) {
      throw new Error('Some items in your cart are missing required information. Please remove them and try again.');
    }

    return {
      orderItems: syncedCart.map(item => ({
        product: item.product._id,
        quantity: item.quantity || 1,
        selectedSize: getOrderItemSize(item)
      })),
      shippingAddress: normalizeShippingAddressForPayment(selectedAddress)
    };
  };
  
  const handleNextStep = () => {
    setCurrentStep(currentStep + 1);
  };
  
  const handlePreviousStep = () => {
    setCurrentStep(currentStep - 1);
  };

  const handlePaymentFailed = (paymentFailure) => {
    navigate('/payment-failed', {
      state: {
        paymentState: paymentFailure?.paymentState || 'failed',
        message: paymentFailure?.message || ''
      }
    });
  };

  const renderStep = () => {
    console.log('Rendering step:', currentStep, 'Order:', order ? 'exists' : 'null', 'Payment completed:', paymentCompleted);
    switch (currentStep) {
      case 1:
        return (
          <AddressForm 
            onAddressSelect={handleAddressSelect} 
            selectedAddress={selectedAddress} 
            onContinue={handleNextStep} 
          />
        );
      case 2:
        return (
          <PaymentForm 
            onPaymentMethodSelect={handlePaymentMethodSelect} 
            selectedPaymentMethod={selectedPaymentMethod} 
            onBack={handlePreviousStep} 
            totalAmount={totalOrderPrice}
            itemsPrice={itemsPrice}
            shippingPrice={shippingPrice}
            taxPrice={taxPrice}
            order={order}
            onCreatePaymentPayload={createPaymentPayload}
            onPaymentFailed={handlePaymentFailed}
          />
        );
      case 3:
        // Ensure we have an order before rendering the confirmation
        if (!order && !loading) {
          console.error('Trying to render confirmation step but order is null');
        }
        return (
          <OrderConfirmation 
            order={order} 
            paymentMethod={selectedPaymentMethod} 
          />
        );
      default:
        return null;
    }
  };
  
  if (loading) {
    return (
      <div className="checkout-container">
        <div className="loading">Processing your order...</div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="checkout-container">
        <div className="error-message">
          <h2>Error</h2>
          <p>{error}</p>
          <button 
            onClick={() => {
              setError('');
              setLoading(false);
              setCurrentStep(1);
              setSelectedPaymentMethod(null);
              console.log('Try Again button clicked - resetting checkout process');
            }} 
            className="back-button"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }
  
  if (authLoading) {
    return (
      <div className="checkout-container">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return null; // Will redirect in useEffect
  }
  
  return (
    <div className="checkout-container">
      <h1 className="checkout-title">Checkout</h1>
      
      <div className="checkout-steps">
        <div className={`step ${currentStep >= 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`}>
          <div className="step-number">1</div>
          <div className="step-title">Shipping</div>
        </div>
        <div className={`step ${currentStep >= 2 ? 'active' : ''} ${currentStep > 2 ? 'completed' : ''}`}>
          <div className="step-number">2</div>
          <div className="step-title">Payment</div>
        </div>
        <div className={`step ${currentStep >= 3 ? 'active' : ''}`}>
          <div className="step-number">3</div>
          <div className="step-title">Confirmation</div>
        </div>
      </div>
      
      <div className={`checkout-content ${currentStep === 2 ? 'checkout-content--payment' : ''}`}>
        {renderStep()}
      </div>
    </div>
  );
};

export default CheckoutPage;
